// src/pages/Clinic/ClinicSubscriptionPage.jsx
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import axiosInstance from '../../axiosInstance';
import { useClinic } from '../../context/ClinicContext';
import {
  Box,
  Container,
  Chip,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Card,
  CardContent,
  Stack,
  useTheme,
  alpha,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const MotionCard = motion(Card);
const MotionBox = motion(Box);

const ClinicSubscriptionPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { authStatus, loading: authLoading, isAuthenticated } = useAuth();
  const { selectedClinicId, activeClinic } = useClinic();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const status = searchParams.get('status');

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      if (!selectedClinicId) {
        setLoading(false);
        return;
      }
      try {
        const res = await axiosInstance.get('/clinica/suscripcion/estado/', {
          signal: controller.signal,
          params: { clinic_id: selectedClinicId },
        });
        setData(res.data);
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [selectedClinicId]);

  const activa = data?.estado === 'Activa';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(
          theme.palette.secondary.main,
          0.05
        )} 100%)`,
        py: 6,
      }}
    >
      <Container maxWidth="lg">
        <AnimatePresence mode="wait">
          <MotionBox
            key="header"
            initial={{ opacity: 0, y: -14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            sx={{
              mb: 4,
              p: 4,
              borderRadius: 6,
              color: 'white',
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              boxShadow: `0 8px 28px ${alpha('#000', 0.2)}`,
            }}
          >
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" justifyContent="space-between">
              <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: 0.2 }}>
                  Suscripción de Clínica
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Administra el plan activo de tu organización.
                </Typography>
              </Box>
              {activeClinic && (
                <Chip
                  label={`Clínica: ${activeClinic?.clinica?.nombre || selectedClinicId}`}
                  sx={{
                    bgcolor: alpha('#fff', 0.15),
                    color: 'white',
                    border: `1px solid ${alpha('#fff', 0.35)}`,
                    fontWeight: 700,
                    height: 36,
                  }}
                />
              )}
            </Stack>
          </MotionBox>

          {status === 'success' && (
            <Alert severity="success" sx={{ mb: 2 }}>
              ¡Suscripción activada con éxito!
            </Alert>
          )}
          {status === 'failed' && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Pago fallido o cancelado.
            </Alert>
          )}

          {!selectedClinicId && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Selecciona una clínica para ver su suscripción.
            </Alert>
          )}

          {selectedClinicId && loading && (
            <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          )}

          {selectedClinicId && !loading && (
            <MotionCard
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45 }}
              elevation={0}
              sx={{
                borderRadius: 4,
                p: 3,
                background: 'white',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                boxShadow: `0 8px 28px ${alpha(theme.palette.primary.main, 0.08)}`,
              }}
            >
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                  <SubscriptionsIcon color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    Estado de la suscripción
                  </Typography>
                </Stack>

                {activa ? (
                  <>
                    <Chip
                      icon={<CheckCircleIcon />}
                      label="Activa"
                      color="success"
                      sx={{ mb: 2, fontWeight: 700 }}
                    />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Plan: {data?.plan?.nombre}
                    </Typography>
                    <Typography sx={{ mt: 1 }}>Inicio: {data?.fecha_inicio || '—'}</Typography>
                    <Typography>Fin: {data?.fecha_fin || '—'}</Typography>
                    <Typography sx={{ mt: 1 }}>
                      Renovación automática: {data?.renovacion_automatica ? 'Sí' : 'No'}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Alert
                      icon={<WarningAmberIcon fontSize="inherit" />}
                      severity="warning"
                      sx={{ mb: 2 }}
                    >
                      La clínica no tiene una suscripción activa.
                    </Alert>
                    <Button
                      variant="contained"
                      onClick={() => navigate('/doctor/clinica/planes')}
                      sx={{
                        borderRadius: 3,
                        fontWeight: 800,
                        px: 3,
                        py: 1.2,
                        boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.28)}`,
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: `0 10px 26px ${alpha(theme.palette.primary.main, 0.42)}`,
                        },
                        transition: 'all 0.25s ease',
                      }}
                    >
                      Ver Planes de Clínica
                    </Button>
                  </>
                )}
              </CardContent>
            </MotionCard>
          )}
        </AnimatePresence>
      </Container>
    </Box>
  );
};

export default ClinicSubscriptionPage;
